import * as React from 'react';

import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';

import { cn } from '../lib/utils';

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'vai-flex vai-h-10 vai-w-full vai-items-center vai-justify-between vai-rounded-md vai-border vai-border-input vai-bg-background vai-px-3 vai-py-2 vai-text-sm vai-ring-offset-background placeholder:vai-text-muted-foreground focus:vai-outline-none focus:vai-ring-2 focus:vai-ring-ring focus:vai-ring-offset-2 disabled:vai-cursor-not-allowed disabled:vai-opacity-50 [&>span]:vai-line-clamp-1',
      className,
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="vai-h-4 vai-w-4 vai-opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      'vai-flex vai-cursor-default vai-items-center vai-justify-center vai-py-1',
      className,
    )}
    {...props}
  >
    <ChevronUp className="vai-h-4 vai-w-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      'vai-flex vai-cursor-default vai-items-center vai-justify-center vai-py-1',
      className,
    )}
    {...props}
  >
    <ChevronDown className="vai-h-4 vai-w-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

type SelectContentProps = React.ComponentPropsWithoutRef<
  typeof SelectPrimitive.Content
> & {
  hideScrollButtons?: boolean;
};

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  SelectContentProps
>(
  (
    {
      className,
      children,
      position = 'popper',
      hideScrollButtons = false,
      ...props
    },
    ref,
  ) => (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          'vai-relative vai-z-[10000] vai-max-h-96 vai-min-w-[8rem] vai-overflow-hidden vai-rounded-md vai-border vai-bg-popover vai-text-popover-foreground vai-shadow-md data-[state=open]:vai-animate-in data-[state=closed]:vai-animate-out data-[state=closed]:vai-fade-out-0 data-[state=open]:vai-fade-in-0 data-[state=closed]:vai-zoom-out-95 data-[state=open]:vai-zoom-in-95 data-[side=bottom]:vai-slide-in-from-top-2 data-[side=left]:vai-slide-in-from-right-2 data-[side=right]:vai-slide-in-from-left-2 data-[side=top]:vai-slide-in-from-bottom-2',
          position === 'popper' &&
            'data-[side=bottom]:vai-translate-y-1 data-[side=left]:vai--translate-x-1 data-[side=right]:vai-translate-x-1 data-[side=top]:vai--translate-y-1',
          className,
        )}
        position={position}
        {...props}
      >
        {!hideScrollButtons ? <SelectScrollUpButton /> : null}
        <SelectPrimitive.Viewport
          className={cn(
            'vai-p-1',
            position === 'popper' &&
              'vai-h-[var(--radix-select-trigger-height)] vai-w-full vai-min-w-[var(--radix-select-trigger-width)]',
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        {!hideScrollButtons ? <SelectScrollDownButton /> : null}
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  ),
);
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(
      'vai-py-1.5 vai-pl-8 vai-pr-2 vai-text-sm vai-font-semibold',
      className,
    )}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'vai-relative vai-flex vai-w-full vai-cursor-default vai-select-none vai-items-center vai-rounded-sm vai-py-1.5 vai-pl-8 vai-pr-2 vai-text-sm vai-outline-none focus:vai-bg-accent focus:vai-text-accent-foreground data-[disabled]:vai-pointer-events-none data-[disabled]:vai-opacity-50',
      className,
    )}
    {...props}
  >
    <span className="vai-absolute vai-left-2 vai-flex vai-h-3.5 vai-w-3.5 vai-items-center vai-justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="vai-h-4 vai-w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>

    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('vai--mx-1 vai-my-1 vai-h-px vai-bg-muted', className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
